import React, { useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';

import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Tooltip from '@mui/material/Tooltip';

import MoodleCoursesNavItem from './MoodleCoursesNavItem';
import { StyledNavItemButton, StyledBadge } from './Sidebar.styles';
import { NavItem, SidebarNavItemProps } from './Sidebar.types';

export const getNavItemKey = (item: NavItem): string =>
  item.searchParams ? `${item.path}?${JSON.stringify(item.searchParams)}` : item.path ?? '';

const buildItemSearch = (item: NavItem, currentPath: string, currentSearch: string): string | undefined => {
  if (!item.searchParams) return undefined;
  const params = new URLSearchParams(currentPath === item.path ? currentSearch : '');
  Object.entries(item.searchParams).forEach(([key, value]) => {
    if (value === null) params.delete(key);
    else params.set(key, value);
  });
  const search = params.toString();
  return search ? `?${search}` : '';
};

const SidebarNavItem: React.FC<SidebarNavItemProps> = ({ item, isActive, isCollapsed, onNavigate, onClose }) => {
  if (item.action === 'moodle-sso') {
    return <MoodleCoursesNavItem item={item} isCollapsed={isCollapsed} onClose={onClose} />;
  }

  if (!item.path) {
    return null;
  }

  const IconComponent = item.Icon;
  const iconProps = { className: 'sidebar-icon', style: { width: '20px', height: '20px' } };
  const itemKey = getNavItemKey(item);
  const location = useLocation();

  const handleClick = useCallback(() => onNavigate?.(itemKey), [itemKey, onNavigate]);

  const content = (
    <StyledNavItemButton
      component={Link as React.ElementType}
      to={{ pathname: item.path, search: buildItemSearch(item, location.pathname, location.search) }}
      state={item.state}
      active={isActive}
      aria-current={isActive ? 'page' : undefined}
      onClick={handleClick}
      sx={{
        justifyContent: isCollapsed ? 'center' : 'flex-start',
        px: isCollapsed ? 1 : 1.5,
      }}
    >
      <ListItemIcon
        sx={{
          minWidth: isCollapsed ? 0 : 40,
          color: 'inherit',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <IconComponent {...iconProps} />
      </ListItemIcon>
      {!isCollapsed && (
        <>
          <ListItemText
            primary={item.text}
            sx={(theme) => ({
              flex: 1,
              '& .MuiListItemText-primary': {
                fontSize: '16px',
                fontWeight: isActive ? 600 : 400,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                [theme.breakpoints.up('xl')]: {
                  fontSize: '18px',
                },
              },
            })}
          />
          {item.badge && <StyledBadge label={item.badge} size="small" />}
        </>
      )}
    </StyledNavItemButton>
  );

  if (isCollapsed) {
    return (
      <Tooltip title={item.badge ? `${item.text} (${item.badge})` : item.text} placement="right" arrow>
        <ListItem disablePadding sx={{ py: 0.5 }}>
          {content}
        </ListItem>
      </Tooltip>
    );
  }

  return (
    <ListItem disablePadding sx={{ py: 0.5 }}>
      {content}
    </ListItem>
  );
};

export default SidebarNavItem;
